import {
  type CSSProperties,
  type JSX,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  RASTERSPALTEN,
  RASTERZEILEN,
  ersteFreieStelle,
  freieFlaechen,
  gleicheLage,
  inLesefolge,
  lege,
  mindestgroesse,
  schritt,
  setzeEin,
  ueberlappen,
  verdichte,
} from '@crm/kontrakte/src/raster';

import { AUFGABEN, type Anordnung, BERICHTSWIDGET, type Feld, type Groesse, type Widget } from '../api/uebersicht';
import { type Aufklappeintrag, Aufklapper } from '../komponenten/Aufklapper';
import { Dialog } from '../komponenten/Dialog';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Sinnbild } from '../komponenten/Sinnbild';
import { sinnbildFuer } from './Widget';

/**
 * DAS DASHBOARD ALS FREIES RASTER (Auftrag #35, SYS-12 i. d. F. AeA-40).
 *
 * ============================================================================
 * DAS RASTER
 * ============================================================================
 *
 * Ab 1024 px vier Spalten, die Zeilen fest so hoch wie eine Kachel
 * (`ZEILE_PX`). Jedes Widget liegt, wo seine Lage es hinlegt: Spalte x,
 * Zeile y, Breite 1–4, Hoehe 1–4. Die Rechnung — ausweichen, Luecken nach
 * oben schliessen, erste freie Stelle — steht in `@crm/kontrakte/src/raster`,
 * derselben Datei, mit der die API prueft und ablegt. Was die Maske beim
 * Loslassen zeigt, ist also, was der Server speichert.
 *
 * Im DOM stehen die Felder in LESEFOLGE (oben links nach unten rechts). Weil
 * sich Luecken nach oben schliessen, ist das zugleich die Folge im Bild — ein
 * Vorleseprogramm und die Tabulatortaste gehen denselben Weg wie das Auge
 * (WCAG 1.3.2, 2.4.3).
 *
 * UNTER 1024 PX zwei Spalten, auf dem Handy eine: Die Felder fliessen in
 * Lesefolge, aus der Lage abgeleitet, und angepasst wird nicht — ein Raster,
 * das man auf 360 px umbaut, stuende auf dem Rechner danach anders da.
 *
 * ============================================================================
 * RUHIG, BIS MAN ANPASST
 * ============================================================================
 *
 * Ausserhalb des Anpassens steht nur der Inhalt da. Ein Feld, das gerade
 * nichts zu zeigen hat („Das steht an" ohne Aufgaben), faellt weg, und die
 * Felder darunter ruecken fuer die Anzeige nach — ohne dass sich die
 * gespeicherte Lage aendert. Im Anpassen bekommt jedes Feld Umriss, Griff,
 * Ecke und Menue, und auch das leere steht da. Der Inhalt ist dann `inert`:
 * Ein Griff daneben soll nicht in eine Kachel springen.
 *
 * ============================================================================
 * ZIEHEN — UND DASSELBE OHNE ZIEHEN (WCAG 2.5.7, NFA-07)
 * ============================================================================
 *
 * ZIEHEN mit Maus und Finger ueber Pointer Events. Griff und Ecke fangen den
 * Zeiger (`setPointerCapture`) und tragen `touch-action: none` — sonst rollte
 * auf dem iPad die Seite, statt dass das Feld wandert. Aus der Lage des
 * Zeigers wird die Zelle; der Griff verschiebt, die Ecke bemisst, die anderen
 * weichen schon waehrend des Ziehens aus. Loslassen legt ab, Escape verwirft.
 * HTML-Ziehen (`draggable`) kennt Safari auf dem iPad nur halb; deshalb nicht.
 *
 * FREIE FLÄCHEN (Auftrag #37): Im Anpassen steht auf jeder
 * freien Zelle, auf der ein Widget liegen bliebe, ein „+" — ein Knopf, mit
 * Tab erreichbar, in Lesefolge zwischen den Feldern. Er öffnet die Auswahl,
 * und das Gewählte kommt GENAU DORT hin: in Vorgabegröße, sonst so groß, wie
 * es passt (`setzeEin`). Der Knopf „Widget oder Bericht einfügen" über dem
 * Raster bleibt und nimmt die erste freie Stelle.
 *
 * AUFZIEHEN (Auftrag #37): Wer auf einem „+" drückt und über weitere freie
 * Zellen zieht, legt die Größe gleich mit fest; die Auswahl setzt das Widget
 * genau in dieses Rechteck (nie unter seine Mindestgröße). Liegt im
 * Rechteck schon ein Feld, sagt die Ansage es, und nichts geschieht. Die
 * Tastatur braucht das nicht: Enter auf einer Fläche setzt in Vorgabegröße
 * ein, und die Größe stellt danach das Menü des Felds.
 *
 * OHNE ZIEHEN: Das Menue am Feld bietet nach links, rechts, oben, unten,
 * Breite, Hoehe und Entfernen — ein Knopf mit Tab, Pfeiltasten und Enter.
 * Griff und Ecke sind deshalb fuer die Tastatur nichts (`aria-hidden`): Sie
 * waeren Tabulatorschritte, die nichts koennen, was das Menue nicht kann.
 * Nach jeder Wahl steht der Fokus wieder auf dem Menue desselben Felds, und
 * die Ansage nennt die neue Lage.
 *
 * ============================================================================
 * WARUM KEIN RASTERBAUSTEIN (Auftrag #35, Punkt 5)
 * ============================================================================
 *
 * react-grid-layout (MIT, gepflegt) waere der naheliegende. Dagegen sprach:
 * Er bringt mit react-draggable und react-resizable rund 40 KB ins Buendel
 * fuer ein Raster aus vier Spalten; er setzt die Lage mit `transform` und
 * absoluter Position, sodass die Reihenfolge im DOM nicht die im Bild ist
 * (WCAG 1.3.2); ein Weg ohne Ziehen (WCAG 2.5.7) fehlt ihm, das Menue muesste
 * ohnehin daneben entstehen; und die Rechnung muesste die API ein zweites
 * Mal haben, um zu pruefen, was sie ablegt. CSS Grid mit Pointer Events
 * braucht keine Inline-Skripte und nichts von aussen (CSP `'self'`), und die
 * Lage kommt aus derselben Datei wie auf dem Server.
 */
export function Feldraster({
  anordnung,
  frei,
  anpassen,
  beschaeftigt,
  inhalt,
  beiAenderung,
  auswahlOffen,
  beiAuswahl,
}: {
  readonly anordnung: Anordnung;
  /** Ab 1024 px: die Lage gilt, und es darf angepasst werden. */
  readonly frei: boolean;
  readonly anpassen: boolean;
  readonly beschaeftigt: boolean;
  /** Was im Feld steht; `null`, wenn es gerade nichts zu zeigen gibt. */
  readonly inhalt: (feld: Feld) => ReactNode | null;
  readonly beiAenderung: (felder: readonly Feld[], ansage: string) => void;
  /** Die Auswahl „Widget oder Bericht einfügen" — der Knopf steht in der Leiste darüber. */
  readonly auswahlOffen: boolean;
  readonly beiAuswahl: (offen: boolean) => void;
}): JSX.Element {
  const raster = useRef<HTMLDivElement>(null);
  const [zug, setzeZug] = useState<Zug | null>(null);
  const [rueckfrage, setzeRueckfrage] = useState<Feld | null>(null);
  const [fokus, setzeFokus] = useState<string | undefined>(undefined);
  /* Die freie Fläche, aus der die Auswahl geöffnet wurde; `null`: der Knopf darüber. */
  const [stelle, setzeStelle] = useState<Stelle | null>(null);
  /* Auftrag #37: ein Rechteck, das gerade über freie Zellen aufgezogen wird. */
  const [aufzug, setzeAufzug] = useState<Aufzug | null>(null);
  /* Nach einem Aufziehen folgt noch der Klick des Knopfs — er öffnet dann nicht ein zweites Mal. */
  const aufgezogen = useRef(false);
  const [hinweis, setzeHinweis] = useState('');
  const bearbeiten = anpassen && frei;

  /* Nach einer Wahl im Menü: der Fokus zurück auf das Menü des Felds. */
  useEffect(() => {
    if (fokus === undefined) return;
    document.getElementById(fokus)?.focus();
  }, [anordnung, fokus]);

  /* Escape verwirft ein laufendes Ziehen. */
  useEffect(() => {
    if (zug === null) return;
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setzeZug(null);
    };
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [zug]);

  /* Das Anpassen endet: Ein halbes Ziehen gilt nicht. */
  useEffect(() => {
    if (!bearbeiten) setzeZug(null);
  }, [bearbeiten]);

  const felder = anordnung.felder;
  const name = (kennung: string): string => namensVon(anordnung.verfuegbar, kennung);

  /*
   * Was gezeichnet wird: im Anpassen jedes Feld in seiner Lage (beim Ziehen
   * die Vorschau), sonst nur die mit Inhalt — nachgerückt, damit ein leeres
   * Feld keine Lücke hinterlässt.
   */
  const inhalte = new Map(felder.map((f) => [f.kennung, inhalt(f)]));
  const gezeigt = bearbeiten
    ? (zug?.vorschau ?? felder)
    : verdichte(felder.filter((f) => inhalte.get(f.kennung) !== null));

  function aendere(neu: readonly Feld[], kennung: string | undefined, ansage: string, fokusNach: string | undefined): void {
    setzeFokus(fokusNach);
    const feld = kennung === undefined ? undefined : neu.find((f) => f.kennung === kennung);
    beiAenderung(neu, feld === undefined ? ansage : `${ansage} ${lageText(name(feld.kennung), feld)}`);
  }

  function entferne(feld: Feld): void {
    const rest = verdichte(felder.filter((f) => f.kennung !== feld.kennung));
    const danach = inLesefolge(rest).find((f) => f.y > feld.y || (f.y === feld.y && f.x > feld.x)) ?? rest[0];
    aendere(rest, undefined, `${name(feld.kennung)} entfernt.`, danach === undefined ? EINFUEGEN_ID : menueId(danach.kennung));
  }

  function fuegeEin(w: Widget): void {
    beiAuswahl(false);
    setzeStelle(null);
    const min = mindestgroesse(w.kennung);
    const neu =
      stelle === null
        ? verdichte([...felder, { kennung: w.kennung, ...w.vorgabe, ...ersteFreieStelle(felder, w.vorgabe) }])
        : setzeEin(
            felder,
            {
              kennung: w.kennung,
              // Aufgezogen: genau diese Größe — nie unter der Mindestgröße der Art.
              vorgabe:
                stelle.groesse === undefined
                  ? w.vorgabe
                  : { breite: Math.max(stelle.groesse.breite, min.breite), hoehe: Math.max(stelle.groesse.hoehe, min.hoehe) },
            },
            stelle,
          );
    aendere(neu, w.kennung, 'Eingefügt.', menueId(w.kennung));
  }

  function oeffneAuswahl(an: Stelle | null): void {
    setzeStelle(an);
    beiAuswahl(true);
  }

  /* ── Aufziehen über freie Zellen (Auftrag #37) ───────────────────────────── */

  function beginneAufzug(e: ReactPointerEvent<HTMLButtonElement>, an: { x: number; y: number }): void {
    if (beschaeftigt || (e.pointerType === 'mouse' && e.button !== 0)) return;
    aufgezogen.current = false;
    const knopf = e.currentTarget;
    if (typeof knopf.setPointerCapture === 'function') knopf.setPointerCapture(e.pointerId);
    setzeAufzug({ zeiger: e.pointerId, start: an, ende: an });
  }

  function ziehtAuf(e: ReactPointerEvent<HTMLDivElement>): void {
    if (aufzug === null || e.pointerId !== aufzug.zeiger) return;
    const z = zelle(e.clientX, e.clientY);
    if (z === null) return;
    const ende = {
      x: Math.min(Math.max(z.x, 0), RASTERSPALTEN - 1),
      y: Math.min(Math.max(z.y, 0), RASTERZEILEN - 1),
    };
    if (ende.x !== aufzug.ende.x || ende.y !== aufzug.ende.y) setzeAufzug({ ...aufzug, ende });
  }

  function laesstAufzugLos(e: ReactPointerEvent<HTMLDivElement>): void {
    if (aufzug === null || e.pointerId !== aufzug.zeiger) return;
    setzeAufzug(null);
    const r = rechteck(aufzug);
    /* Eine einzelne Zelle ist ein Klick — den nimmt der Knopf selbst. */
    if (r.breite === 1 && r.hoehe === 1) return;
    /* Nur für den Klick, der gleich auf dasselbe Loslassen folgt — danach gilt Enter wieder. */
    aufgezogen.current = true;
    window.setTimeout(() => {
      aufgezogen.current = false;
    }, 0);
    if (felder.some((f) => ueberlappen(f, r))) {
      setzeHinweis('Die aufgezogene Fläche ist nicht frei. Ziehen Sie nur über Flächen mit „+".');
      return;
    }
    setzeHinweis('');
    oeffneAuswahl({ x: r.x, y: r.y, groesse: { breite: r.breite, hoehe: r.hoehe } });
  }

  /* Die freien Flächen — nur im Anpassen und nicht, während gezogen wird. */
  const flaechen = bearbeiten && zug === null ? freieFlaechen(felder) : [];

  /* ── Ziehen und Bemessen (Pointer Events, Maus und Finger) ─────────────── */

  /** Die Zelle unter dem Zeiger — aus der Lage des Rasters und den festen Maßen. */
  function zelle(clientX: number, clientY: number): { x: number; y: number } | null {
    const r = raster.current?.getBoundingClientRect();
    if (r === undefined || r.width <= 0) return null;
    const spalte = (r.width - (RASTERSPALTEN - 1) * ABSTAND_PX) / RASTERSPALTEN;
    return {
      x: Math.floor((clientX - r.left) / (spalte + ABSTAND_PX)),
      y: Math.floor((clientY - r.top) / (ZEILE_PX + ABSTAND_PX)),
    };
  }

  function greife(e: ReactPointerEvent<HTMLSpanElement>, feld: Feld, art: Zug['art']): void {
    if (beschaeftigt || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const z = zelle(e.clientX, e.clientY);
    if (z === null) return;
    e.preventDefault();
    e.stopPropagation();
    const griff = e.currentTarget;
    if (typeof griff.setPointerCapture === 'function') griff.setPointerCapture(e.pointerId);
    setzeZug({ art, kennung: feld.kennung, zeiger: e.pointerId, versatz: { x: z.x - feld.x, y: z.y - feld.y }, vorschau: felder });
  }

  function zieht(e: ReactPointerEvent<HTMLDivElement>): void {
    if (zug === null || e.pointerId !== zug.zeiger) return;
    const z = zelle(e.clientX, e.clientY);
    const alt = felder.find((f) => f.kennung === zug.kennung);
    if (z === null || alt === undefined) return;
    /* Immer vom Stand vor dem Griff aus gerechnet — sonst wanderte die Vorschau mit jedem Zug weiter. */
    const vorschau =
      zug.art === 'ziehen'
        ? lege(felder, zug.kennung, { x: z.x - zug.versatz.x, y: z.y - zug.versatz.y })
        : lege(felder, zug.kennung, { breite: z.x - alt.x + 1, hoehe: z.y - alt.y + 1 });
    if (!gleicheLage(vorschau, zug.vorschau)) setzeZug({ ...zug, vorschau });
  }

  function laesstLos(e: ReactPointerEvent<HTMLDivElement>): void {
    if (zug === null || e.pointerId !== zug.zeiger) return;
    setzeZug(null);
    if (gleicheLage(zug.vorschau, felder)) return;
    aendere(zug.vorschau, zug.kennung, zug.art === 'ziehen' ? 'Verschoben.' : 'Größe geändert.', undefined);
  }

  return (
    <>
      <div
        ref={raster}
        className={`feldraster ${frei ? 'feldraster--frei' : 'feldraster--folge'}${bearbeiten ? ' feldraster--anpassen' : ''}${zug === null ? '' : ' feldraster--zieht'}`}
        style={{ gridAutoRows: `${String(ZEILE_PX)}px`, gap: `${String(ABSTAND_PX)}px` }}
        onPointerMove={(e) => {
          zieht(e);
          ziehtAuf(e);
        }}
        onPointerUp={(e) => {
          laesstLos(e);
          laesstAufzugLos(e);
        }}
        onPointerCancel={() => {
          setzeZug(null);
          setzeAufzug(null);
        }}
      >
        {inLesefolge<Feld | Flaeche>([
          ...gezeigt,
          ...flaechen.map((f): Flaeche => ({ ...f, breite: 1, hoehe: 1, flaeche: true })),
        ]).map((eintrag) => {
          if ('flaeche' in eintrag) {
            return (
              <button
                key={`flaeche-${String(eintrag.x)}-${String(eintrag.y)}`}
                type="button"
                className="rasterflaeche"
                style={{ gridColumn: `${String(eintrag.x + 1)} / span 1`, gridRow: `${String(eintrag.y + 1)} / span 1` }}
                aria-haspopup="dialog"
                aria-label={`Freie Fläche Spalte ${String(eintrag.x + 1)}, Zeile ${String(eintrag.y + 1)}: Widget oder Bericht einfügen`}
                disabled={beschaeftigt}
                onPointerDown={(e) => beginneAufzug(e, { x: eintrag.x, y: eintrag.y })}
                onClick={() => {
                  if (aufgezogen.current) {
                    aufgezogen.current = false;
                    return;
                  }
                  oeffneAuswahl({ x: eintrag.x, y: eintrag.y });
                }}
              >
                <Sinnbild gewicht="light" name="plus" />
              </button>
            );
          }
          const feld = eintrag;
          const was = inhalte.get(feld.kennung) ?? null;
          const klasse = [
            'rasterfeld',
            `rasterfeld--b${String(feld.breite)}`,
            `rasterfeld--h${String(feld.hoehe)}`,
            bearbeiten ? 'rasterfeld--anpassen' : '',
            zug?.kennung === feld.kennung ? 'rasterfeld--gezogen' : '',
          ]
            .filter((k) => k !== '')
            .join(' ');
          const lage: CSSProperties = frei
            ? {
                gridColumn: `${String(feld.x + 1)} / span ${String(feld.breite)}`,
                gridRow: `${String(feld.y + 1)} / span ${String(feld.hoehe)}`,
              }
            : {};
          return (
            <div
              key={feld.kennung}
              className={klasse}
              style={lage}
              data-feld={feld.kennung}
              {...(bearbeiten ? { role: 'group', 'aria-label': name(feld.kennung) } : {})}
            >
              {bearbeiten && (
                <div className="rasterfeld__kopf">
                  <span
                    className="rasterfeld__griff"
                    aria-hidden="true"
                    title="Ziehen zum Verschieben"
                    data-griff="ziehen"
                    onPointerDown={(e) => greife(e, feld, 'ziehen')}
                  >
                    <Sinnbild gewicht="light" name="dots-six-vertical" />
                  </span>
                  {/* Auftrag #37: dasselbe Sinnbild wie im Kopf der Karte. */}
                  <span className="rasterfeld__sinnbild">
                    <Sinnbild gewicht="light" name={sinnbildFuer(feld.kennung)} />
                  </span>
                  <span className="rasterfeld__name">{name(feld.kennung)}</span>
                  <Aufklapper
                    beschriftung={`${name(feld.kennung)}: Feld anpassen`}
                    kennung={menueId(feld.kennung)}
                    knopf={<Sinnbild gewicht="light" name="dots-three" />}
                    knopfklasse="rasterfeld__menue"
                    rechtsbuendig
                    eintraege={menue(feld)}
                  />
                </div>
              )}
              <div className="rasterfeld__inhalt" inert={bearbeiten}>
                {was ?? (
                  <p className="rasterfeld__leer">
                    Zurzeit nichts zu zeigen — das Feld erscheint, sobald es etwas gibt.
                  </p>
                )}
              </div>
              {bearbeiten && (
                <span
                  className="rasterfeld__ecke"
                  aria-hidden="true"
                  title="Ziehen ändert die Größe"
                  data-griff="ecke"
                  onPointerDown={(e) => greife(e, feld, 'ecke')}
                />
              )}
            </div>
          );
        })}
        {aufzug !== null && (
          /* Das aufgezogene Rechteck — rot umrandet, wenn es ein Feld trifft. */
          <div
            className={`rasteraufzug${felder.some((f) => ueberlappen(f, rechteck(aufzug))) ? ' rasteraufzug--belegt' : ''}`}
            aria-hidden="true"
            style={{
              gridColumn: `${String(rechteck(aufzug).x + 1)} / span ${String(rechteck(aufzug).breite)}`,
              gridRow: `${String(rechteck(aufzug).y + 1)} / span ${String(rechteck(aufzug).hoehe)}`,
            }}
          />
        )}
      </div>

      {hinweis !== '' && (
        <p className="hinweiszeile" role="status">
          {hinweis}
        </p>
      )}

      {auswahlOffen && (
        <Widgetauswahl
          frei={anordnung.verfuegbar.filter((w) => !felder.some((f) => f.kennung === w.kennung))}
          unterzeile={
            stelle === null
              ? 'Das Feld kommt an die erste freie Stelle.'
              : stelle.groesse === undefined
                ? `Das Feld kommt an Spalte ${String(stelle.x + 1)}, Zeile ${String(stelle.y + 1)} — in seiner Größe, soweit Platz ist.`
                : `Das Feld kommt an Spalte ${String(stelle.x + 1)}, Zeile ${String(stelle.y + 1)}, ${String(stelle.groesse.breite)} × ${String(stelle.groesse.hoehe)} — nie kleiner als seine Mindestgröße.`
          }
          beiWahl={fuegeEin}
          beiSchliessen={() => {
            setzeStelle(null);
            beiAuswahl(false);
          }}
        />
      )}

      {rueckfrage !== null && (
        /*
          „DAS STEHT AN" ENTFERNEN FRAGT NACH (SYS-12, Auftrag #35). Die Liste
          erscheint nur, wenn etwas zu tun ist; wer sie entfernt, sieht auch
          den überfälligen Posten von morgen nicht mehr. Das soll er wissen,
          bevor er es tut — und wie er sie zurückholt.
        */
        <Rueckfrage
          frage={'„Das steht an" entfernen?'}
          tat="Entfernen"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={() => {
            const feld = rueckfrage;
            setzeRueckfrage(null);
            entferne(feld);
          }}
        >
          <p>
            Die offenen Aufgaben erscheinen dann nicht mehr auf Ihrem Dashboard — auch keine
            überfälligen Posten und keine offenen Ressourcenlücken. Über „Widget oder Bericht
            einfügen" holen Sie das Feld jederzeit zurück.
          </p>
        </Rueckfrage>
      )}
    </>
  );

  /** Das Menü am Feld — alles, was Griff und Ecke können (WCAG 2.5.7, NFA-07). */
  function menue(feld: Feld): readonly Aufklappeintrag[] {
    const min = mindestgroesse(feld.kennung);
    const schritte = (['links', 'rechts', 'oben', 'unten'] as const).flatMap((richtung) => {
      const neu = schritt(felder, feld.kennung, richtung);
      return neu === null
        ? []
        : [{ text: `Nach ${richtung}`, beiWahl: () => aendere(neu, feld.kennung, 'Verschoben.', menueId(feld.kennung)) }];
    });
    const groesse = (
      werte: readonly number[],
      ist: number,
      wort: (n: number) => string,
      lage: (n: number) => Partial<Feld>,
    ): Aufklappeintrag[] =>
      werte.map((n) => ({
        text: wort(n),
        vorlesen: `${wort(n)}${n === ist ? ', gewählt' : ''}`,
        ...(n === ist ? { zusatz: 'gewählt' } : {}),
        beiWahl: () => aendere(lege(felder, feld.kennung, lage(n)), feld.kennung, 'Größe geändert.', menueId(feld.kennung)),
      }));
    return [
      ...schritte,
      ...groesse(von(min.breite, RASTERSPALTEN), feld.breite, (n) => `${String(n)} ${n === 1 ? 'Spalte' : 'Spalten'} breit`, (n) => ({ breite: n })),
      ...groesse(von(min.hoehe, 4), feld.hoehe, (n) => `${String(n)} ${n === 1 ? 'Zeile' : 'Zeilen'} hoch`, (n) => ({ hoehe: n })),
      {
        /*
          Nur „Das steht an" fragt nach (CRM-104). Jedes andere Feld ist eine
          Ansicht: Widget und Bericht bleiben, wie sie sind, und „Widget oder
          Bericht einfügen" holt das Feld zurück — wie „Auf Vorgabe
          zurücksetzen" tastet das nichts Gespeichertes an.
        */
        text: 'Entfernen',
        beiWahl: () => (feld.kennung === AUFGABEN ? setzeRueckfrage(feld) : entferne(feld)),
      },
    ];
  }
}

/**
 * Die Auswahl aus Widgets und Berichten, eigenen und geteilten (#27).
 * Angeboten wird, was die API zur Verfügung stellt und noch nicht im Raster
 * liegt; das Recht prüft die API (SYS-12). Das neue Feld kommt in seiner
 * Vorgabegröße an die erste freie Stelle.
 */
function Widgetauswahl({
  frei,
  unterzeile,
  beiWahl,
  beiSchliessen,
}: {
  readonly frei: readonly Widget[];
  readonly unterzeile: string;
  readonly beiWahl: (w: Widget) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const widgets = frei.filter((w) => !w.kennung.startsWith(BERICHTSWIDGET));
  const berichte = frei.filter((w) => w.kennung.startsWith(BERICHTSWIDGET));
  const zeile = (w: Widget): JSX.Element => (
    <li className="anordnung__zeile" key={w.kennung}>
      <span className="anordnung__name">
        {w.beschriftung}
        {w.nurBeiBedarf && <small> — erscheint nur, wenn es etwas zu zeigen gibt</small>}
      </span>
      <button
        className="knopf knopf--klein"
        type="button"
        aria-label={`${w.beschriftung} einfügen`}
        onClick={() => beiWahl(w)}
      >
        Einfügen
      </button>
    </li>
  );
  return (
    <Dialog
      titel="Widget oder Bericht einfügen"
      unterzeile={unterzeile}
      beiSchliessen={beiSchliessen}
    >
      {frei.length === 0 && (
        <p className="hinweiszeile">Alles, was Ihnen zur Verfügung steht, ist schon auf dem Dashboard.</p>
      )}
      {widgets.length > 0 && (
        <>
          <h3>Widgets</h3>
          <ul className="anordnung">{widgets.map(zeile)}</ul>
        </>
      )}
      {berichte.length > 0 && (
        <>
          <h3>Berichte</h3>
          <ul className="anordnung">{berichte.map(zeile)}</ul>
        </>
      )}
    </Dialog>
  );
}

/** Wohin die Auswahl einsetzt; mit `groesse`, wenn aufgezogen (Auftrag #37). */
type Stelle = { readonly x: number; readonly y: number; readonly groesse?: Groesse };

type Aufzug = {
  readonly zeiger: number;
  readonly start: { readonly x: number; readonly y: number };
  readonly ende: { readonly x: number; readonly y: number };
};

/** Das Rechteck zwischen Anfang und Ende, gleich in welche Richtung gezogen. */
function rechteck(a: Aufzug): { x: number; y: number; breite: number; hoehe: number } {
  const x = Math.min(a.start.x, a.ende.x);
  const y = Math.min(a.start.y, a.ende.y);
  return { x, y, breite: Math.abs(a.start.x - a.ende.x) + 1, hoehe: Math.min(Math.abs(a.start.y - a.ende.y) + 1, 4) };
}

/** Eine freie Fläche im Raster — ein „+" auf einer Zelle. */
type Flaeche = { readonly x: number; readonly y: number; readonly breite: 1; readonly hoehe: 1; readonly flaeche: true };

type Zug = {
  /** Der Griff verschiebt, die Ecke bemisst. */
  readonly art: 'ziehen' | 'ecke';
  readonly kennung: string;
  readonly zeiger: number;
  /** Wo im Feld der Zeiger griff, in Zellen — damit das Feld nicht unter ihn springt. */
  readonly versatz: { readonly x: number; readonly y: number };
  /** Die Lage, wie sie beim Loslassen gälte. */
  readonly vorschau: readonly Feld[];
};

/** Die feste Zeilenhöhe: etwa eine Kachel (Auftrag #35). */
export const ZEILE_PX = 128;
/** Der Abstand zwischen den Feldern — wie `--kartenabstand`. */
export const ABSTAND_PX = 10;

/** Der Knopf „Widget oder Bericht einfügen" in der Leiste des Arbeitsplatzes. */
export const EINFUEGEN_ID = 'feldraster-einfuegen';

function menueId(kennung: string): string {
  return `feldmenue-${kennung}`;
}

function namensVon(verfuegbar: readonly Widget[], kennung: string): string {
  return verfuegbar.find((w) => w.kennung === kennung)?.beschriftung ?? kennung;
}

function von(unten: number, oben: number): number[] {
  return Array.from({ length: oben - unten + 1 }, (_, i) => unten + i);
}

/** Die Ansage nach einer Änderung — die neue Lage in Worten. */
function lageText(name: string, f: Feld): string {
  return `${name}: Spalte ${String(f.x + 1)}, Zeile ${String(f.y + 1)}, ${String(f.breite)} ${
    f.breite === 1 ? 'Spalte' : 'Spalten'
  } breit, ${String(f.hoehe)} ${f.hoehe === 1 ? 'Zeile' : 'Zeilen'} hoch.`;
}
